// web/src/components/MatchFormBits.tsx
// Shared form atoms for the two-stage match flow (MatchAdd → MatchRecord)
// and the existing MatchEdit form. Originally lived inside MatchAdd; lifted
// out 2026-05-19 when the match flow was split into setup + record.

import type { ReactNode } from 'react';

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h2
        style={{
          fontSize: 16,
          marginBottom: 12,
          fontFamily: 'var(--font-serif)',
          color: 'var(--green)',
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          display: 'block',
          fontSize: 14,
          color: 'var(--muted)',
          marginBottom: 4,
          fontWeight: 500,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export function ChipRow({
  value,
  options,
  onChange,
  allowClear,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
  allowClear?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            type="button"
            key={opt}
            onClick={() => onChange(allowClear && active ? '' : opt)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: `1px solid ${active ? 'var(--green)' : 'var(--border)'}`,
              background: active ? 'var(--green)' : 'transparent',
              color: active ? 'var(--ink-on-green)' : 'var(--ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer',
              textTransform: 'capitalize',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function MultiChipRow({
  values,
  options,
  onChange,
  max,
}: {
  values: string[];
  options: readonly string[];
  onChange: (v: string[]) => void;
  max?: number;
}) {
  const toggle = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter((v) => v !== opt));
    } else {
      if (max && values.length >= max) return;
      onChange([...values, opt]);
    }
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const active = values.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            onClick={() => toggle(opt)}
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              border: `1px solid ${active ? 'var(--green)' : 'var(--border)'}`,
              background: active ? 'var(--green)' : 'transparent',
              color: active ? 'var(--ink-on-green)' : 'var(--ink)',
              fontFamily: 'inherit',
              fontSize: 14,
              cursor: 'pointer',
              textTransform: 'capitalize',
              minHeight: 36,
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, accentColor: 'var(--green)' }}
      />
      <span style={{ fontSize: 14, color: 'var(--ink)' }}>{label}</span>
    </label>
  );
}

export const MATCH_POSITIONS = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward', 'Other'] as const;
